import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Trash2, Star, Loader2, User } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { API, API_BASE_URL } from '../config/api';
import { safeJson } from '../utils/safeJson';
import { useAuth } from '../context/AuthContext';

function resolveUrl(src) {
  if (!src) return null;
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('data:')) return src;
  if (src.startsWith('/api')) return `${API_BASE_URL}${src}`;
  return `${API_BASE_URL}${src.startsWith('/') ? '' : '/'}${src}`;
}

/**
 * PhotoLightbox - Full-screen gallery viewer with navigation and actions
 *
 * Legacy mode (images array):
 *   <PhotoLightbox images={[{url}]} initialIndex={0} onClose={fn} />
 *
 * Gallery mode (userId):
 *   <PhotoLightbox userId="user_xxx" userName="Name" initialPhotoUrl="/api/..." onClose={fn} onAvatarChange={fn} />
 */
export default function PhotoLightboxComponent({ userId, userName, initialPhotoUrl, onClose, onAvatarChange, images, initialIndex = 0 }) {
  const { user, getAuthHeaders } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [actionLoading, setActionLoading] = useState(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const touchStartX = useRef(null);

  // Legacy mode — static image list passed as prop
  const isLegacyMode = !!images;
  const isOwner = !isLegacyMode && user?.user_id === userId;

  useEffect(() => {
    if (isLegacyMode) {
      setPhotos((images || []).map((img, i) => ({
        media_id: `_legacy_${i}`,
        url: typeof img === 'string' ? img : img.url,
        is_active_avatar: false,
      })));
      setCurrentIndex(initialIndex);
      setLoading(false);
      return;
    }

    const fetchGallery = async () => {
      try {
        const endpoint = isOwner ? `${API}/media/library` : `${API}/media/library/${userId}`;
        const headers = isOwner ? getAuthHeaders() : {};
        const res = await fetch(endpoint, { headers });
        const result = await safeJson(res);
        if (result.ok && result.data?.photos?.length > 0) {
          setPhotos(result.data.photos);
          if (initialPhotoUrl) {
            const idx = result.data.photos.findIndex(p =>
              p.url === initialPhotoUrl || resolveUrl(p.url) === resolveUrl(initialPhotoUrl)
            );
            if (idx >= 0) setCurrentIndex(idx);
          }
        } else if (initialPhotoUrl) {
          setPhotos([{ media_id: '_profile', url: initialPhotoUrl, is_active_avatar: true }]);
        }
      } catch {
        if (initialPhotoUrl) {
          setPhotos([{ media_id: '_profile', url: initialPhotoUrl, is_active_avatar: true }]);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, [userId, isOwner, isLegacyMode, images, initialIndex, getAuthHeaders, initialPhotoUrl]);

  // Keyboard nav
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') nav(-1);
      if (e.key === 'ArrowRight') nav(1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const nav = useCallback((dir) => {
    if (photos.length <= 1) return;
    setImgLoaded(false);
    setCurrentIndex(prev => {
      const n = prev + dir;
      if (n < 0) return photos.length - 1;
      if (n >= photos.length) return 0;
      return n;
    });
  }, [photos.length]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) nav(diff > 0 ? 1 : -1);
    touchStartX.current = null;
  };

  const handleSetAvatar = async () => {
    const photo = photos[currentIndex];
    if (!photo || photo.media_id.startsWith('_')) return;
    setActionLoading('avatar');
    try {
      const res = await fetch(`${API}/media/${photo.media_id}/set-avatar`, {
        method: 'PUT', headers: getAuthHeaders(),
      });
      const result = await safeJson(res);
      if (result.ok) {
        toast.success('Profile picture updated!');
        setPhotos(prev => prev.map((p, i) => ({ ...p, is_active_avatar: i === currentIndex })));
        if (onAvatarChange) onAvatarChange(photo.url);
      } else {
        toast.error(result.error || 'Failed to update');
      }
    } catch { toast.error('Failed to update'); }
    finally { setActionLoading(null); }
  };

  const handleDelete = async () => {
    const photo = photos[currentIndex];
    if (!photo || photo.media_id.startsWith('_')) return;
    setActionLoading('delete');
    try {
      const res = await fetch(`${API}/media/${photo.media_id}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      const result = await safeJson(res);
      if (result.ok) {
        toast.success('Photo deleted');
        const remaining = photos.filter((_, i) => i !== currentIndex);
        if (remaining.length === 0) {
          if (onAvatarChange) onAvatarChange(null);
          onClose();
          return;
        }
        setPhotos(remaining);
        setCurrentIndex(prev => Math.min(prev, remaining.length - 1));
        if (photo.is_active_avatar && onAvatarChange) {
          const next = remaining.find(p => p.is_active_avatar) || remaining[0];
          onAvatarChange(next?.url || null);
        }
      } else {
        toast.error(result.error || 'Failed to delete');
      }
    } catch { toast.error('Failed to delete'); }
    finally { setActionLoading(null); }
  };

  const current = photos[currentIndex];
  const currentUrl = current ? resolveUrl(current.url) : null;
  const canAct = isOwner && current && !current.media_id.startsWith('_');

  const content = (
    <div
      className="fixed inset-0 z-[9999] flex flex-col"
      data-testid="photo-lightbox"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="absolute inset-0 bg-black/95 backdrop-blur-sm" />

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between px-4 py-3 sm:px-6">
        <div className="flex items-center gap-3 min-w-0">
          {!isLegacyMode && (
            <div className="w-8 h-8 rounded-full bg-muted/50 overflow-hidden flex-shrink-0">
              {initialPhotoUrl ? (
                <img src={resolveUrl(initialPhotoUrl)} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><User className="w-4 h-4 text-muted-foreground/60" /></div>
              )}
            </div>
          )}
          <span className="text-white font-medium text-sm truncate">{userName || 'Photos'}</span>
          {photos.length > 1 && (
            <span className="text-muted-foreground/70 text-sm" data-testid="photo-counter">
              {currentIndex + 1} / {photos.length}
            </span>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onClose();
          }}
          data-testid="lightbox-close"
          className="w-9 h-9 rounded-full bg-muted/50 hover:bg-muted/60 flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Photo area */}
      <div
        className="relative z-10 flex-1 flex items-center justify-center px-4 sm:px-16 overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {loading ? (
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        ) : currentUrl ? (
          <>
            {!imgLoaded && <Loader2 className="absolute w-6 h-6 animate-spin text-orange-500" />}
            <img
              key={currentUrl}
              src={currentUrl}
              alt={`Photo ${currentIndex + 1}`}
              data-testid="lightbox-photo"
              className={`max-h-[70vh] max-w-full rounded-xl object-contain shadow-2xl select-none transition-opacity duration-200 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgLoaded(true)}
              draggable={false}
            />
          </>
        ) : (
          <div className="text-muted-foreground/50 text-center">
            <User className="w-16 h-16 mx-auto mb-2 opacity-30" />
            <p>No photos</p>
          </div>
        )}

        {photos.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); nav(-1); }}
              data-testid="lightbox-prev"
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-background/50 hover:bg-background/70 flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); nav(1); }}
              data-testid="lightbox-next"
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-background/50 hover:bg-background/70 flex items-center justify-center transition-colors"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="relative z-10 px-4 py-3 sm:px-6">
        {photos.length > 1 && photos.length <= 20 && (
          <div className="flex justify-center gap-1.5 mb-3">
            {photos.map((_, i) => (
              <button
                key={i}
                onClick={() => { setImgLoaded(false); setCurrentIndex(i); }}
                className={`h-2 rounded-full transition-all ${i === currentIndex ? 'bg-orange-500 w-4' : 'bg-muted/70 hover:bg-muted/300 w-2'}`}
              />
            ))}
          </div>
        )}

        {canAct && (
          <div className="flex items-center justify-center gap-3">
            {!current.is_active_avatar ? (
              <Button
                size="sm"
                data-testid="lightbox-set-avatar"
                onClick={handleSetAvatar}
                disabled={!!actionLoading}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                {actionLoading === 'avatar' ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Star className="w-4 h-4 mr-1.5" />}
                Set as Profile Picture
              </Button>
            ) : (
              <span className="text-orange-400 text-sm flex items-center gap-1.5">
                <Star className="w-4 h-4" /> Current Profile Picture
              </span>
            )}
            <Button
              size="sm"
              variant="outline"
              data-testid="lightbox-delete"
              onClick={handleDelete}
              disabled={!!actionLoading}
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
            >
              {actionLoading === 'delete' ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
              Delete
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

/**
 * ClickableProfilePhoto - Wraps children in a clickable container (legacy compat)
 */
export function ClickableProfilePhoto({ src, alt, className, children, onClick }) {
  return (
    <div
      className={`cursor-pointer ${className || ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`View ${alt || 'profile'} photos`}
    >
      {children}
    </div>
  );
}

// Named re-export so `import { PhotoLightbox } from './PhotoLightbox'` works
const PhotoLightboxNamed = PhotoLightboxComponent;
export { PhotoLightboxNamed as PhotoLightbox };
